const missingReceiverErrorMessage =
  'Could not establish connection. Receiving end does not exist.';

export const extensions = {
  finalytic: {
    ids: [
      'lmjjdicajpllfgekjbnnjobadadidoih',
      'jkcopfjpdildkbilmhcaegojcjnedmof',
    ],
    store_url:
      'https://chrome.google.com/webstore/detail/finalytic-connect-web-ext/lmjjdicajpllfgekjbnnjobadadidoih',
  },
  vrplatform: {
    ids: [
      'hphpodppjfegboiaddkpkceedjjjjjma',
      'dgnfieblfdbmehilhfkgepgigedgaklg',
    ],
    store_url:
      'https://chrome.google.com/webstore/detail/vrplatform-connect-web-ex/hphpodppjfegboiaddkpkceedjjjjjma',
  },
} as const;

export type ExtensionPlatform = keyof typeof extensions;

export const getExtension = (platformName?: string) => {
  const key =
    platformName && platformName in extensions
      ? (platformName as ExtensionPlatform)
      : 'vrplatform';
  const extension = extensions[key];
  return {
    key,
    extension: {
      ...extension,
      id: extension.ids[0],
    },
  };
};

type ExtensionMessage = {
  message: string;
  data: object;
};

type ExtensionReply = {
  exists?: boolean;
};

type RuntimeLastError = {
  message?: string;
};

export type ExtensionRuntime = {
  sendMessage?: (
    extensionId: string,
    payload: { data: object },
    options: object,
    callback: (reply?: ExtensionReply) => void
  ) => void;
  getLastError?: () => RuntimeLastError | undefined;
};

type SendMessageToExtensionIdsArgs = {
  callback?: (reply: { exists: boolean }) => void;
  extensionIds: readonly string[];
  message: ExtensionMessage;
  runtime: ExtensionRuntime | undefined;
};

type MessageDeliveryFailureReason =
  | 'missing_receiver'
  | 'runtime_error'
  | 'runtime_unavailable';

export type MessageDeliveryResult = {
  delivered: boolean;
  reason?: MessageDeliveryFailureReason;
  lastErrorMessage?: string;
};

export async function sendMessageToExtensionIds({
  callback,
  extensionIds,
  message,
  runtime,
}: SendMessageToExtensionIdsArgs): Promise<MessageDeliveryResult> {
  if (!runtime?.sendMessage || !extensionIds.length) {
    callback?.({ exists: false });
    return {
      delivered: false,
      reason: 'runtime_unavailable',
    };
  }

  let runtimeErrorMessage: string | undefined;

  for (const extensionId of extensionIds) {
    const deliveryResult = await new Promise<MessageDeliveryResult>(
      (resolve) => {
        runtime.sendMessage?.(
          extensionId,
          {
            data: {
              ...message.data,
              message: message.message,
            },
          },
          {},
          (reply) => {
            const lastError = runtime.getLastError?.();

            if (lastError?.message === missingReceiverErrorMessage) {
              resolve({
                delivered: false,
                reason: 'missing_receiver',
              });
              return;
            }

            if (lastError) {
              resolve({
                delivered: false,
                reason: 'runtime_error',
                lastErrorMessage: lastError.message,
              });
              return;
            }

            callback?.({ exists: !!reply?.exists });
            resolve({ delivered: true });
          }
        );
      }
    );

    if (deliveryResult.delivered) return { delivered: true };

    if (deliveryResult.reason === 'runtime_error') {
      runtimeErrorMessage =
        deliveryResult.lastErrorMessage ?? runtimeErrorMessage;
    }
  }

  callback?.({ exists: false });
  if (runtimeErrorMessage) {
    return {
      delivered: false,
      reason: 'runtime_error',
      lastErrorMessage: runtimeErrorMessage,
    };
  }

  return {
    delivered: false,
    reason: 'missing_receiver',
  };
}
